import { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";

import "./PackageDetailEdit.css";
import Package from "./Package";
import Product from "./Product";
import ModalFileUpload from "../../components/ModalFileUpload/ModalFileUpload";
import CommonDeleteModal from "../../components/commonDeleteModal/CommonDeleteModal";
import CommonSuccessModal from "../../components/commonSuccessModal/CommonSuccessModal";
import CommonCKEditor from "../../components/commonCKEditor/CommonCKEditor";

import {
  API_STATE,
  ASSET_LOCATION_MAPPING,
  ASSET_TYPE_MAPPING,
  LABEL_DELETE_ASSET,
  FORM_TYPE,
  LANGUAGE_OPTIONS,
  STATUS_CODE_RESPONSE_OK,
  MESSAGE_ERROR_INPUT_REQUIRED,
  DOMAIN, REGION_SG
} from "../../utils/constant";
import { isEmpty, isEmptyObj } from "../../utils/commonUtil";

import { deleteAsset, uploadAsset } from "../../redux/assetSlice";
import {
  fetchCodeDictionaries,
  fetchPackageProductsById,
} from "../../redux/managePackageSlice";
import {
  fetchPackage,
  resetPackageValue,
  setError,
  setValue,
  updatePackage,
} from "../../redux/packageSlice";
import { createPackages } from "../../redux/packagesSlice";

function PackageDetailEdit({
  packageId,
  formType,
  initLanguage,
  handleComplete,
  handleBack,
}) {
  const dispatch = useDispatch();

  const TAB_PACKAGE_KEY = {
    basicInfo: "basicInfo",
    product: "product",
  };

  useEffect(() => {
    dispatch(
      fetchCodeDictionaries({
        region: REGION_SG,
        type: DOMAIN,
      })
    );
  }, [dispatch]);

  const TAB_LIST_MAPPING = formType === FORM_TYPE.CREATE
    ? [
      {
        id: TAB_PACKAGE_KEY.basicInfo,
        label: "Basic Info",
        order: 1
      }
    ]
    : [
      {
        id: TAB_PACKAGE_KEY.basicInfo,
        label: "Basic Info",
        order: 1
      },
      {
        id: TAB_PACKAGE_KEY.product,
        label: "Product",
        order: 2
      }
    ];

  const PACKAGE_MODEL_TEMPLATE = [
    {
      id: "menuName",
      label: "Package name",
      placeholder: {
        EN: "Enter package name in EN",
        CN: "Enter package name in CN",
      },
      type: "text",
      required: true,
    },
    {
      id: "menuDescription",
      label: "Package description",
      placeholder: {
        EN: "Enter package description in EN",
        CN: "Enter package description in CN",
      },
      type: "text",
    },
    {
      id: "titleImageFileId",
      label: "Title Image",
      placeholder: {
        EN: "Enter title image in EN",
        CN: "Enter title image in CN",
      },
      type: "file",
      acceptTypes: ".jpg,.png,",
    },
    {
      id: "type",
      label: "Type",
      type: "radioGroup",
      options: [
        { value: "regional", label: "Regional" },
        { value: "singleDestination", label: "Single Destination" }
      ],
      required: false,
    },
    {
      id: "technicalSpecs",
      label: "Technical Specifications",
      type: "editor",
      required: false,
    },
  ];

  const { codeDictionaries, dictionaryLoading } = useSelector(
    (state) => state.managePackage
  );
  const { data: packageData, status } = useSelector((state) => state.package);

  const [activeTab, setActiveTab] = useState(TAB_PACKAGE_KEY.basicInfo);
  const [language, setLanguage] = useState(initLanguage);
  const [isLoading, setIsLoading] = useState(false);
  const [isShowDeleteAsset, setIsShowDeleteAsset] = useState(null);
  const [isShowAlert, setIsShowAlert] = useState(null);
  const [isAlertSuccess, setIAlertSuccess] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [formData, setFormData] = useState({
    selectedProducts: [],
    selectedDomainType: "",
  });

  useEffect(() => {
    if (formType === FORM_TYPE.EDIT) {
      dispatch(fetchPackage({ packageId, language }));
    }
    if (formType === FORM_TYPE.CREATE) {
      dispatch(resetPackageValue({ language }));
    }
  }, [formType, language, packageId, dispatch]);

  const toggleLanguage = (selectedLanguage) => {
    if (formType === FORM_TYPE.CREATE && selectedLanguage === "CN") {
      return;
    }
    setLanguage(selectedLanguage);
    if (formType === FORM_TYPE.EDIT) {
      dispatch(fetchPackage({ packageId, language: selectedLanguage }));
    }
    if (formType === FORM_TYPE.CREATE) {
      dispatch(resetPackageValue({ language: selectedLanguage }));
    }
  };

  const handleInput = (key, newValue) => {
    dispatch(setValue({ key, value: newValue }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const getProductTypeOptions = useCallback(() => {
    if (!codeDictionaries?.resdata?.data) return [];

    const languageData = codeDictionaries.resdata.data[language];

    if (!languageData || !Array.isArray(languageData)) return [];

    const options = [];

    languageData.forEach((item) => {
      if (
        item.type === DOMAIN &&
        item.codes &&
        Array.isArray(item.codes)
      ) {
        item.codes.forEach((codeItem) => {
          options.push({
            value: codeItem.code,
            label: codeItem.value,
          });
        });
      }
    });

    return options;
  }, [codeDictionaries, language]);

  const productTypeOptions = getProductTypeOptions();

  useEffect(() => {
    if (productTypeOptions.length > 0 && !selectedProductType) {
      setSelectedProductType(productTypeOptions[0]);
    }
  }, [productTypeOptions, selectedProductType]);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      if (activeTab === TAB_PACKAGE_KEY.basicInfo) {
        let error = validationData();
        if (isEmptyObj(error)) {
          dispatch(setError(null));

          let response;

          if (formType === FORM_TYPE.EDIT) {
            response = await dispatch(
              updatePackage({ uuid: packageId, language, payload: packageData })
            );

            if (response?.payload?.status === STATUS_CODE_RESPONSE_OK) {
              handleAlertShow("Package updated successfully!", true);
            } else if (response?.error) {
              handleAlertShow(response.error.message, false);
            }
          }

          if (formType === FORM_TYPE.CREATE) {
            const createPayload = {
              ...packageData,
              languages: ["EN", "CN"],
              region: REGION_SG
            };

            response = await dispatch(createPackages(createPayload));

            if (response?.payload?.status === STATUS_CODE_RESPONSE_OK) {
              handleAlertShow("Package created successfully!", true);
            } else if (response?.error) {
              handleAlertShow(response.error.message, false);
            }
          }
        } else {
          dispatch(setError(error));
          setIsLoading(false);
        }
      }

      if (activeTab === TAB_PACKAGE_KEY.product) {
        const requestBody = {
          domainType: "GENERAL",
          productMasterUuids: formData.selectedProducts,
        };

        try {
          const response = await dispatch(
            fetchPackageProductsById({
              packageId,
              requestBody
            })
          );

          if (response?.payload?.status === STATUS_CODE_RESPONSE_OK) {
            handleAlertShow("Package products updated successfully!", true);
          } else if (response?.error) {
            handleAlertShow(response.error.message, false);
          }
        } catch (error) {
          console.error("Failed to save package products:", error);
          handleAlertShow("Failed to save package products", false);
        }
      }
    } catch (error) {
      console.error("Save operation failed:", error);
      handleAlertShow("Save operation failed", false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAlertShow = (message, isSuccess) => {
    setIsShowAlert(message);
    setIAlertSuccess(isSuccess);
  };

  const handleAlertClose = () => {
    setIsShowAlert(null);
    setIAlertSuccess(null);
  };

  useEffect(() => {
    if (isShowAlert && isAlertSuccess) {
      const timer = setTimeout(() => {
        handleAlertClose();
        handleComplete();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isShowAlert, isAlertSuccess]);

  const validationData = () => {
    let error = {};

    PACKAGE_MODEL_TEMPLATE.forEach((template) => {
      if (template.required && isEmpty(packageData[template.id])) {
        error[template.id] = MESSAGE_ERROR_INPUT_REQUIRED;
      }
    });

    if (activeTab === TAB_PACKAGE_KEY.product &&
      formData.selectedProducts.length > 0 &&
      !selectedProductType) {
      error.productType = true;
    }

    return error;
  };

  const updateProductData = useCallback(
    (selectedProducts, selectedDomainType) => {
      setFormData((prev) => ({
        ...prev,
        selectedProducts,
        selectedDomainType,
      }));
    },
    []
  );

  // #region FILE UPLOAD
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState(null);

  const selectedKey = useRef(null);

  const handleFileUpload = (file) => {
    if (!file) {
      setUploadErrorMessage("Please upload file.");
      return;
    }
    setIsLoading(true);

    const formData = new FormData();

    if (formType === FORM_TYPE.EDIT) {
      formData.append("uuid", packageId);
    }

    formData.append(
      "assetLocation",
      ASSET_LOCATION_MAPPING[selectedKey.current]
    );
    formData.append("assetType", ASSET_TYPE_MAPPING.package);
    formData.append("file", file);

    const response = dispatch(uploadAsset(formData));
    response
      .then((res) => {
        if (res?.payload) {
          const file = res.payload.resdata.fileId;
          handleInput(selectedKey.current, file);
        }
        if (res?.error) {
          handleAlertShow(res.error.message, false);
        }
        setIsLoading(false);
        setShowFileUploadModal(false);
        selectedKey.current = null;
      })
      .catch((err) => {
        handleAlertShow(err, false);
        setIsLoading(false);
        setShowFileUploadModal(false);
        selectedKey.current = null;
      });
  };

  const handleShowUploadFile = (id, tab) => {
    setShowFileUploadModal(true);
    selectedKey.current = id;
  };

  const handleShowDeleteFile = (id, tab) => {
    setIsShowDeleteAsset(LABEL_DELETE_ASSET);
    selectedKey.current = id;
  };

  const handleDeleteFile = () => {
    const response = dispatch(deleteAsset(packageData[selectedKey.current]));
    response
      .then((res) => {
        if (res?.payload) {
          handleInput(selectedKey.current, null);
        }
        if (res?.error) {
          if (res.error.message.includes("No active document")) {
            handleInput(selectedKey.current, null);
          }
          handleAlertShow(res.error.message, false);
        }
        setIsShowDeleteAsset(null);
        selectedKey.current = null;
      })
      .catch((err) => {
        handleAlertShow(err, false);
        setIsShowDeleteAsset(null);
        selectedKey.current = null;
      });
  };

  const handleCancelUploadFile = () => {
    setShowFileUploadModal(false);
    selectedKey.current = null;
  };
  // #endregion

  return (
    <div className="create-user-container">
      <div className="package-wrapper">
        <div className="package-section-title">
          {formType === FORM_TYPE.CREATE ? "Create Package" : 'Edit Package'}
        </div>

        <div className="package-language-toggle hidden-language-toggle">
          {LANGUAGE_OPTIONS.map((option) => (
            <button
              key={option.id}
              onClick={() => toggleLanguage(option.value)}
              className={`package-language-option ${language === option.value ? "package-language-active" : ""
                } ${formType === FORM_TYPE.CREATE && option.value === "CN"
                  ? "package-language-disabled"
                  : ""
                }`}
              disabled={formType === FORM_TYPE.CREATE && option.value === "CN"}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="package-detail-edit-container">
          <div className="tabs-navigation">
            {TAB_LIST_MAPPING.map((tab, index) => (
              <button
                key={`tab_${index}`}
                className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tab-content">
            {activeTab === TAB_PACKAGE_KEY.basicInfo &&
              status === API_STATE.SUCCEEDED && (
                <Package
                  templateList={PACKAGE_MODEL_TEMPLATE}
                  language={language}
                  onInputChange={handleInput}
                  onShowUploadFile={(id) =>
                    handleShowUploadFile(id, TAB_PACKAGE_KEY.basicInfo)
                  }
                  onShowDeleteFile={(id) =>
                    handleShowDeleteFile(id, TAB_PACKAGE_KEY.basicInfo)
                  }
                  packageData={packageData}
                />
              )}
            {activeTab === TAB_PACKAGE_KEY.product && (
              <Product
                language={language}
                selectedProductType={selectedProductType}
                onUpdate={updateProductData}
                initialSelectedProducts={formData.selectedProducts}
                initialSelectedDomainType={formData.selectedDomainType}
                packageId={packageId}
              />
            )}
          </div>
        </div>

        <div className="button-group">
          <button className="package-detail-edit-back" onClick={handleBack}>
            Back
          </button>

          <button className="package-detail-edit-save" onClick={handleSave}>
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <CommonDeleteModal
        show={!isEmpty(isShowDeleteAsset)}
        onHide={() => setIsShowDeleteAsset(null)}
        onConfirm={handleDeleteFile}
      />

      <CommonSuccessModal
        showModal={!isEmpty(isShowAlert)}
        handleClose={handleAlertClose}
        message={isShowAlert}
        status={isAlertSuccess}
      />

      <ModalFileUpload
        show={showFileUploadModal}
        handleCancel={handleCancelUploadFile}
        handleUpload={(file) => handleFileUpload(file)}
        isLoading={isLoading}
        errorMessage={uploadErrorMessage}
      />
    </div>
  );
}

export default PackageDetailEdit;